import {
  InterventionCategory,
  Sector,
  SubSector,
  DataType,
  AllDataType,
} from '@/types';

const interventionAreaLabels = {
  'public-transit-electrification': 'Public transport electrification',
  'residential-building-standards': 'Residential building standards',
  'residential-vkt-change': 'Change in per-resident travel',
  'residential-retrofits': 'Residential retrofits',
  'non-residential-retrofits': 'Non-residential retrofits',
  'non-residential-building-standards': 'Non-residential building standards',
  'residential-mode-shift': 'Mode shift',
  'ev-takeup': 'Electric vehicle take-up',
  'waste-reduction': 'Per-person waste reduction',
  'recyclable-packaging': 'Recyclable packaging',
  'recyclable-material-management': 'Source separation',
  'recyclable-ewaste': 'E-waste management',
  organics: 'Organic waste separation',
  'waste-to-energy': 'Waste to energy',
};

const interventionAreaKeyOrder = [
  'residential-building-standards',
  'non-residential-building-standards',
  'residential-retrofits',
  'non-residential-retrofits',
  'residential-vkt-change',
  'residential-mode-shift',
  'public-transit-electrification',
  'ev-takeup',
  'waste-reduction',
  'recyclable-packaging',
  'recyclable-material-management',
  'recyclable-ewaste',
  'organics',
  'waste-to-energy',
];

const interventionAreaDescriptions = {
  'public-transit-electrification':
    'The rate at which public transport, particularly buses, will be electrified',
  'residential-building-standards':
    'Roll-out of higher sustainability requirements for new residential buildings',
  'residential-vkt-change':
    // eslint-disable-next-line max-len
    'Decreases due to higher job containment, increased walkability, etc., or increases due to urban sprawl and car dependence',
  'residential-retrofits':
    // eslint-disable-next-line max-len
    'Targets for impacting the existing building stock by upgrading appliances and/or switching gas appliances for their electrical counterparts',
  'non-residential-building-standards':
    'Roll-out of higher sustainability requirements for new non-residential buildings',
  'non-residential-retrofits':
    // eslint-disable-next-line max-len
    'Targets for impacting the existing building stock by upgrading appliances and/or switching gas appliances for their electrical counterparts',
  'residential-mode-shift':
    'The proportion of car trips, in km travelled, that will be replaced by public transport',
  'ev-takeup': 'The proportion of all cars that will be electric',
  'waste-reduction':
    'Due to less packaging, phasing out of single-use plastic, etc.',
  'recyclable-packaging':
    'The proportion of remaining packaging that is recyclable, reusable or compostable',
  'recyclable-material-management':
    'The proportion of recyclable packaging that is recovered and diverted from landfill',
  'recyclable-ewaste':
    'The proportion of e-waste that is recovered, recycled and/or diverted from landfill',
  organics: 'The proportion of organic waste that is recovered and composted',
  'waste-to-energy':
    'The proportion of remaining landfill waste that is processed in an energy recovery facility',
};

const interventionAreaCategories = {
  'residential-building-standards': 'energy',
  'residential-retrofits': 'energy',
  'non-residential-retrofits': 'energy',
  'non-residential-building-standards': 'energy',
  'public-transit-electrification': 'transport',
  'residential-vkt-change': 'transport',
  'residential-mode-shift': 'transport',
  'ev-takeup': 'transport',
  'waste-reduction': 'waste',
  'recyclable-packaging': 'waste',
  'recyclable-material-management': 'waste',
  'recyclable-ewaste': 'waste',
  organics: 'waste',
  'waste-to-energy': 'waste',
};

const renderInterventionCategory = (c: InterventionCategory) =>
  ({
    energy: 'Stationary energy',
    transport: 'Transport',
    waste: 'Waste',
    ippu: 'IPPU',
    agriculture: 'Agriculture',
    forestry: 'Forestry',
    development: 'Development',
  })[c] || c;

const interventionCategories: InterventionCategory[] = [
  'energy',
  'transport',
  'waste',
  'ippu',
  'agriculture',
  'forestry',
  'development',
];

const renderSector = (s: Sector) =>
  ({
    residential: 'Residential',
    'health-education': 'Health & Education',
    'commercial-retail': 'Commercial & Retail',
    manufacturing: 'Manufacturing',
    agriculture: 'Agriculture',
    forestry: 'Forestry',
    infrastructure: 'Infrastructure',
  })[s] || s;

const renderSubSector = (s: SubSector) =>
  ({
    'multi-unit-dwellings': 'Multi unit dwellings',
    'single-dwellings': 'Single dwellings',
    commercial: 'Commercial',
    retail: 'Retail',
    education: 'Education',
    health: 'Health',
    manufacturing: 'Manufacturing',
    agriculture: 'Agriculture',
    forestry: 'Forestry',
    'air-travel': 'Air travel',
    'rail-freight': 'Rail freight',
    'marine-freight': 'Marine freight',
  })[s] || s;

const renderDataType = (s: DataType) =>
  ({
    population: 'Population',
    dwellings: 'Dwellings',
    employment: 'Employment',
  })[s] || s;

const renderAllDataType = (s: AllDataType) =>
  ({
    electricity: 'Electricity',
    gas: 'Gas',
    lpg: 'LPG',
    coal: 'Coal',
    ippu: 'IPPU',
    petrol: 'Transport petrol',
    diesel: 'Transport diesel',
    'rail-freight': 'Rail freight',
    livestock: 'Livestock',
    horticulture: 'Horticulture',
    waste: 'Waste',
    wastewater: 'Wastewater',
    'exotic-forestry': 'Exotic forestry',
    population: 'Population',
    dwellings: 'Dwellings',
    employment: 'Employment',
  })[s] || s;

export {
  interventionAreaCategories,
  interventionAreaDescriptions,
  interventionAreaLabels,
  interventionAreaKeyOrder,
  renderInterventionCategory,
  renderSector,
  renderAllDataType,
  renderSubSector,
  renderDataType,
  interventionCategories,
};
